import { signal } from '@preact/signals';

/**
 * Sleep timer — auto-pauses playback after a chosen interval.
 *
 * Two scheduling modes:
 *   - 'minutes' — fires after N wall-clock minutes (the usual "5/10/15/30
 *     minutes" preset).
 *   - 'end-of-chapter' — fires when the current chapter finishes; lets the
 *     listener finish a scripture passage rather than chopping mid-verse.
 *
 * The 'end-of-chapter' mode is honored by the controller's `onChapterEnd`,
 * which calls `consumeEndOfChapter()` to suppress the usual auto-advance.
 */

export const SLEEP_PRESETS_MINUTES = [5, 10, 15, 30, 60] as const;
export type SleepPresetMinutes = (typeof SLEEP_PRESETS_MINUTES)[number];

type Mode =
  | { kind: 'minutes'; minutes: number; deadlineMs: number }
  | { kind: 'end-of-chapter' };

export const sleepTimer = signal<Mode | null>(null);

/** Wall-clock seconds remaining; -1 when the active mode isn't time-based. */
export const sleepRemainingSec = signal<number>(-1);

let intervalId: number | null = null;
let pauseFn: (() => void) | null = null;

export function registerPauseFn(fn: () => void): void {
  pauseFn = fn;
}

export function startSleepTimer(minutes: number): void {
  cancelSleepTimer();
  const deadlineMs = Date.now() + minutes * 60_000;
  sleepTimer.value = { kind: 'minutes', minutes, deadlineMs };
  tickRemaining();
  intervalId = (setInterval as typeof setInterval)(() => {
    if (!sleepTimer.value || sleepTimer.value.kind !== 'minutes') return;
    if (Date.now() >= sleepTimer.value.deadlineMs) {
      cancelSleepTimer();
      pauseFn?.();
      return;
    }
    tickRemaining();
  }, 1000) as unknown as number;
}

export function scheduleEndOfChapter(): void {
  cancelSleepTimer();
  sleepTimer.value = { kind: 'end-of-chapter' };
  sleepRemainingSec.value = -1;
}

export function cancelSleepTimer(): void {
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
  sleepTimer.value = null;
  sleepRemainingSec.value = -1;
}

/**
 * Called by the controller's chapter-end hook. Returns true if the sleep
 * timer wanted to fire (= suppress auto-advance + pause).
 */
export function consumeEndOfChapter(): boolean {
  if (!sleepTimer.value || sleepTimer.value.kind !== 'end-of-chapter') return false;
  cancelSleepTimer();
  pauseFn?.();
  return true;
}

function tickRemaining(): void {
  const t = sleepTimer.value;
  if (!t || t.kind !== 'minutes') {
    sleepRemainingSec.value = -1;
    return;
  }
  sleepRemainingSec.value = Math.max(0, Math.ceil((t.deadlineMs - Date.now()) / 1000));
}
